/* istanbul ignore file */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { BladeNameT as BladeNameType, IErrorActionPayloadType } from './types';
import Blades from './blades/Blades';
import { RootState } from '../../stories/store';

export type BladeNameT = string;

export type BladeDirectionT = 'Up' | 'Down';

export type NavigationStepInfo = {
    currentStep: number;
    totalSteps: number;
}

export type OnboardingFlowStateT = {
    appInited: boolean;
    activatingBladeName: BladeNameT | null;
    currentBladeName: BladeNameT | null;
    blades: BladeNameT[];
    direction: BladeDirectionT;
    stepInfo: NavigationStepInfo;
}

export const initialState: OnboardingFlowStateT = {
    appInited: false,
    activatingBladeName: null,
    currentBladeName: null,
    blades: [],
    direction: 'Down',
    stepInfo: {
        currentStep: -1,
        totalSteps: -1,
    },
};

export const navigationSlice = createSlice({
    name: 'navigation',
    initialState,
    reducers: {
        startAppInit: (state) => {
            state.appInited = false;
        },
        startAppInitCompleted: (state) => {
            state.appInited = true;
        },
        startPrevBladeNavigation: (state, action: PayloadAction<BladeNameT>) => {
            state.activatingBladeName = action.payload;
            state.direction = 'Up';
        },
        startBladeNavigation: (state, action: PayloadAction<BladeNameT>) => {
            state.activatingBladeName = action.payload;
            state.direction = 'Down';
        },
        completeBladeNavigation: (state, action: PayloadAction<BladeNameT>) => {
            state.activatingBladeName = null;
            state.currentBladeName = action.payload;
        },
        updateNavigationStepInfo: (state, action: PayloadAction<NavigationStepInfo>) => {
            state.stepInfo.currentStep = action.payload.currentStep;
            state.stepInfo.totalSteps = action.payload.totalSteps;
        },
        addBlade: (state, action: PayloadAction<BladeNameT>) => {
            if (state.blades.length) {
                const findPrevIndex = state.blades.findIndex((blade) => (
                    Blades[blade as BladeNameType].prev === action.payload
                ));

                const findNextIndex = state.blades.findIndex((blade) => (
                    Blades[blade as BladeNameType].next === action.payload
                ));

                // Add new blade based on prev or next
                // It will be pushed to the end if not found
                if (findPrevIndex > -1) {
                    state.blades.splice(findPrevIndex, 0, action.payload);
                } else if (findNextIndex > -1) {
                    state.blades.splice(findNextIndex + 1, 0, action.payload);
                } else {
                    state.blades.push(action.payload);
                }
            } else {
                state.blades.push(action.payload);
            }
        },
        removeBlade: (state, action: PayloadAction<BladeNameT>) => {
            state.blades.splice(state.blades.indexOf(action.payload), 1);
        },
        removeSpecificBlades: (state, action: PayloadAction<BladeNameT[]>) => {
            action.payload.forEach((bladeName) => {
                const index = state.blades.indexOf(bladeName);

                if (index > -1) {
                    state.blades.splice(index, 1);
                }
            });
        },
        removeExistingBladesAfter: (state, action: PayloadAction<BladeNameType>) => {
            const index = state.blades.indexOf(action.payload);

            if (index > -1) {
                const retainArr = state.blades.slice(0, index + 1);

                state.blades = [...retainArr];
            }
        },
        removeExistingBladesBefore: (state, action: PayloadAction<BladeNameT>) => {
            const index = state.blades.indexOf(action.payload);

            if (index > -1) {
                const retainArr = state.blades.slice(index);

                state.blades = [...retainArr];
            }
        },
        handleError: (state, action: PayloadAction<IErrorActionPayloadType>) => state,
        handleSubmitError: (state, action: PayloadAction<IErrorActionPayloadType>) => state,
    },
});

export const {
    startAppInit,
    startAppInitCompleted,
    startBladeNavigation,
    completeBladeNavigation,
    updateNavigationStepInfo,
    addBlade,
    removeBlade,
    removeExistingBladesBefore,
    removeSpecificBlades,
    removeExistingBladesAfter,
    startPrevBladeNavigation,
    handleError,
    handleSubmitError,
} = navigationSlice.actions;

export const selectAppInitState = (state: RootState): boolean => (
    state.navigation.appInited
);

export const selectActivatingBladeName = (state: RootState): BladeNameT | null => (
    state.navigation.activatingBladeName
);

export const selectBlades = (state: RootState): BladeNameT[] => (
    state.navigation.blades
);

export const selectCurrentBladeAllowNavigateToPrevious = (state: RootState, currentBladeName: BladeNameType): boolean => (
    currentBladeName ? !!Blades[currentBladeName].prev : true
);

export const selectCurrentBlade = (state: RootState): BladeNameType | null => (
    state.navigation.currentBladeName as BladeNameType | null
);

export const selectBladeDirection = (state: RootState): BladeDirectionT | null => (
    state.navigation.direction
);
export const selectNavigationStepInfo = (state: RootState): NavigationStepInfo => (
    state.navigation.stepInfo
);

export default navigationSlice.reducer;
